import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import { KatLitElement, Keys, register } from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './selection-card.lit.scss';

/**
 * @component {kat-selection-card} KatSelectionCard will allow users to select either a single option from several others (Single select card: Radio button) or allow users to evaluate each option independently and select one, multiple, or none (Multi-select card: Checkbox).
 * @guideline Do Use selection type to determine single-select (radio) or multiselect (check) selection card.
 * @guideline Do Use Selection Cards when there is a small area in which a large or varying amount of information must be selected
 * @guideline Do Use simple, succinctly-messaged and clearly-designed information that can be understood quickly
 * @guideline Do Keep the number of selection cards shown to a manageable amount that does not require scrolling on desktop. Too many can be overwhelming.
 * @guideline Dont Don't use a selection card if the information requires more room to be  communicated clearly, or takes several steps to complete
 * @guideline Dont Don't overload a selection card with information or messaging, rendering it difficult to understand
 * @status Production
 * @theme flo
 * @example BasicCheck {
 * "selection-type": "check",
 * "title": "Check Selection",
 * "content": "
 * <span slot=\"subtitle\">
 *      Subtitle
 *  </span>
 *   <div>
 *      Body content
 *  </div>
 * <div slot=\"footer\">
 *      Footer
 *  </div>
 * "}
 * @example BasicRadio {
 * "selection-type": "radio",
 * "title": "Radio Selection",
 * "content": "
 * <span slot=\"subtitle\">
 *      Subtitle
 *  </span>
 *   <div>
 *      Body content
 *  </div>
 * <div slot=\"footer\">
 *      Footer
 *  </div>
 * "}
 * @example Image {
 * "selection-type": "check",
 * "title": "Title as Property",
 * "image": true,
 * "content": "
 * <img slot=\"image\" src=\"https://m.media-amazon.com/images/G/01/katal/internal/SquareCat1.jpg\"></img>
 * <span slot=\"subtitle\">
 *      Subtitle
 *  </span>
 *   <div>
 *      Body content
 *  </div>
 * <div slot=\"footer\">
 *      Footer
 *  </div>
 * "}
 * @example Disabled {
 * "selection-type": "check",
 * "title": "Title as Property",
 * "disabled": true,
 * "content": "
 * <span slot=\"subtitle\">
 *      Subtitle
 *  </span>
 *   <div>
 *      Body content
 *  </div>
 * <div slot=\"footer\">
 *      Footer
 *  </div>
 * "}
 * @slot default Elements without a named slot attribute; appear in the card body
 * @slot title Title element; takes precedence over title property
 * @slot action Action element
 * @slot subtitle Subtitle element
 * @slot footer Footer element
 * @slot image Image element
 */
@register('kat-selection-card')
export class KatSelectionCard extends KatLitElement {
  /**
   * Title text that will be displayed to the user. NOTE: title slot takes precedence over title property
   * @required false
   */
  @property({ reflect: false })
  title: string;

  /** Setting to true will remove the default padding in the body and/or default slot */
  @property({ attribute: 'no-body-padding' })
  noBodyPadding = false;

  /** Setting to true will add slot for image content */
  @property()
  image: boolean;

  /** Set the selection type to "check" for kat-checkbox multiselect or "radio" for kat-radiobutton single select
   * @required true
   */
  @property({ attribute: 'selection-type' })
  selectionType: string;

  /** Setting to true will disable the selection card */
  @property()
  disabled: boolean;

  /** Setting to true will check the selection-card */
  @property()
  checked: boolean;

  static get styles() {
    return [baseStyles, styles];
  }

  handleCheckSelection() {
    const checkElement = this.shadowRoot.querySelector('kat-checkbox');
    if (!checkElement.checked) {
      checkElement.checked = false;
      this.checked = false;
    } else {
      checkElement.checked = true;
      checkElement.focus();
      this.checked = true;
    }
  }

  firstUpdated() {
    if (this.selectionType === 'check') {
      this.shadowRoot
        .querySelector('kat-checkbox')
        .addEventListener('change', this.handleCheckSelection.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.selectionType === 'check') {
      this.shadowRoot
        .querySelector('kat-checkbox')
        .removeEventListener('change', this.handleCheckSelection.bind(this));
    }
  }

  handleRadioSelection() {
    const radioElement = this.shadowRoot.querySelector('kat-radiobutton');
    radioElement.click();
    radioElement.focus();
    this.checked = true;
  }

  onKeyDown(e) {
    e.preventDefault();
    if (e.keyCode === Keys.Enter) {
      if (this.selectionType === 'radio') {
        this.handleRadioSelection();
      }
    }
  }

  onClick() {
    if (!this.disabled) {
      if (this.selectionType === 'radio') {
        this.handleRadioSelection();
      }
      if (this.selectionType === 'check') {
        const checkElement = this.shadowRoot.querySelector('kat-checkbox');
        if (checkElement.checked) {
          checkElement.checked = false;
          this.checked = false;
        } else {
          checkElement.checked = true;
          checkElement.focus();
          this.checked = true;
        }
      }
    }
  }

  renderSelectable() {
    let selectable;
    if (this.selectionType === 'radio') {
      selectable = html`
        <kat-radiobutton
          class="selectable"
          part="selectable"
          ?checked=${this.checked}
          .disabled=${this.disabled}
        ></kat-radiobutton>
      `;
    }
    if (this.selectionType === 'check') {
      selectable = html`<kat-checkbox
        class="selectable"
        ?checked=${this.checked}
        .disabled=${this.disabled}
      ></kat-checkbox>`;
    }
    return selectable;
  }

  render() {
    const result = checkSlots(this);
    const hasHeader = this.title || result.title;
    const hasFooter = result.footer;
    return html`
      <div class="container" @click=${this.onClick} @keypress=${this.onKeyDown}>
        <div class="left">
          <div class="image"><slot name="image"></slot></div>
        </div>
        <div class="right">
          ${!hasHeader
            ? nothing
            : html`
                <div class="header">
                  <div class="title">
                    <slot name="title">${this.title || nothing}</slot>
                  </div>
                  ${this.renderSelectable()}
                </div>
              `}
          <div class="subtitle"><slot name="subtitle"></slot></div>
          <div class="body">
            <slot></slot>
          </div>
          ${!hasFooter
            ? nothing
            : html`<div class="footer">
                <slot name="footer"></slot>
              </div>`}
        </div>
      </div>
    `;
  }
}
